<template>
  <div class="user-activated-modal bg-white modal-card">
    <header class="user-activated-modal__header">
      <div>
        <h2>Ajouter un compte actif</h2>
        <p>Le compte pourra se connecter immédiatement avec le mot de passe défini.</p>
      </div>
      <button type="button" aria-label="Fermer" :disabled="loading" @click="$parent.close()">
        <i class="fas fa-times" />
      </button>
    </header>

    <form id="activated-user-form" class="grid grid-cols-4 gap-5 p-6" @submit.prevent="create">
      <t-input-group
        label="Email"
        class="col-span-4"
        :variant="{ danger: emailError }"
        :feedback="emailError"
      >
        <t-input
          v-model.trim="form.email"
          type="email"
          required
          autocomplete="off"
          :variant="{ danger: emailError }"
          @blur="validateEmail"
        />
      </t-input-group>

      <t-input-group
        label="Mot de passe"
        class="col-span-4"
        :variant="{ danger: passwordError }"
        :feedback="passwordError"
      >
        <t-input
          v-model="form.password"
          type="password"
          required
          autocomplete="new-password"
          :variant="{ danger: passwordError }"
          @blur="validatePassword"
        />
      </t-input-group>

      <t-input-group
        label="Confirmation du mot de passe"
        class="col-span-4"
        :variant="{ danger: passwordError }"
      >
        <t-input
          v-model="form.password_confirmation"
          type="password"
          required
          autocomplete="new-password"
          :variant="{ danger: passwordError }"
          @blur="validatePassword"
        />
      </t-input-group>
    </form>
    <footer class="user-activated-modal__footer">
      <button
        type="button"
        class="user-activated-modal__cancel"
        :disabled="loading"
        @click="$parent.close()"
      >
        Annuler
      </button>
      <button
        type="submit"
        form="activated-user-form"
        class="user-activated-modal__submit"
        :disabled="loading"
      >
        <i v-if="loading" class="fas fa-spinner fa-spin" />
        Créer le compte
      </button>
    </footer>
  </div>
</template>

<script>
import { http } from '@/services/api.service'

export default {
  name: 'UserCreateActivatedModal',
  data () {
    return {
      loading: false,
      emailError: '',
      passwordError: '',
      form: {
        email: '',
        password: '',
        password_confirmation: ''
      }
    }
  },
  methods: {
    validateEmail () {
      const email = this.form.email.trim()
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      this.emailError = email && emailRegex.test(email)
        ? ''
        : 'Veuillez saisir une adresse email valide'

      return !this.emailError
    },
    validatePassword () {
      if (!this.form.password) {
        this.passwordError = 'Veuillez renseigner un mot de passe'
        return false
      }

      if (this.form.password !== this.form.password_confirmation) {
        this.passwordError = 'Les mots de passe ne correspondent pas'
        return false
      }

      this.passwordError = ''
      return true
    },
    validateForm () {
      return this.validateEmail() && this.validatePassword()
    },
    async create () {
      if (!this.validateForm() || this.loading) {
        return
      }

      this.loading = true

      try {
        const { data } = await http.post('/user', this.form)
        this.$emit('created', data)
        this.$parent.close()
      } catch (err) {
        this.$buefy.notification.open({
          duration: 3000,
          message: err.response?.data?.message || 'Erreur lors de la création du compte.',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true,
          icon: 'server'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.user-activated-modal {
  width: 32rem;
  max-width: calc(100vw - 2rem);
  overflow: hidden;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.user-activated-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.user-activated-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.user-activated-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
  line-height: 1.4;
}

.user-activated-modal__header > button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.user-activated-modal__header > button:hover:not(:disabled) {
  background: #f8fafc;
  color: #111827;
}

.user-activated-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.user-activated-modal__cancel,
.user-activated-modal__submit {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
}

.user-activated-modal__cancel {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #475569;
}

.user-activated-modal__cancel:hover:not(:disabled) {
  background: #f1f5f9;
}

.user-activated-modal__submit {
  background: #2563eb;
  color: #fff;
}

.user-activated-modal__submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.user-activated-modal__header > button:disabled,
.user-activated-modal__cancel:disabled,
.user-activated-modal__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.user-activated-modal__header > button:focus-visible,
.user-activated-modal__cancel:focus-visible,
.user-activated-modal__submit:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}
</style>
